import { motion } from 'framer-motion';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Mail } from 'lucide-react';

const LIVING_WEBSITE_PRODUCTS: Record<string, { name: string; blurb: string }> = {
  'living-website-lite': {
    name: 'Living Website Lite',
    blurb: 'We will email you within 2 business days to schedule kickoff for your avatar embed and knowledge base.',
  },
  'living-website-pro': {
    name: 'Living Website Pro',
    blurb: 'We will email you within 2 business days to begin your full site-aware living website build.',
  },
  'build-deposit': {
    name: 'Project deposit',
    blurb: 'Your deposit is confirmed. We will email you within 2 business days to schedule kickoff on the build calendar.',
  },
  'build-full': {
    name: 'Full project payment',
    blurb: 'Your full project payment is confirmed. We will email you within 2 business days to schedule kickoff on the build calendar.',
  },
  'blood-evaluation-deposit': {
    name: 'BLOOD evaluation deposit',
    blurb: 'Your evaluation deposit is confirmed. A member of our team will email you within 2 business days with the NDA and technology briefing to begin your BLOOD partnership evaluation.',
  },
  'self-dog-p1': {
    name: 'SELF Dog P1',
    blurb: 'Your SELF Dog P1 order is confirmed. A member of our team will email you within 2 business days to confirm shipping, customization options, and deployment planning.',
  },
  'self-dog-e1': {
    name: 'SELF Dog E1',
    blurb: 'Your SELF Dog E1 order is confirmed. A member of our team will email you within 2 business days to confirm your Pro or Edu configuration, shipping, and deployment planning.',
  },
  'self-bot-x1': {
    name: 'SELF Bot X1',
    blurb: 'Your SELF Bot X1 order is confirmed. A member of our team will email you within 2 business days to coordinate enterprise delivery, deployment planning, and any custom software integration.',
  },
  'self-bot-p1': {
    name: 'SELF Bot P1',
    blurb: 'Your SELF Bot P1 order is confirmed. A member of our team will email you within 2 business days to confirm shipping, deployment planning, and any software integration options.',
  },
};

const SuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const productKey = searchParams.get('product');
  const livingProduct = productKey ? LIVING_WEBSITE_PRODUCTS[productKey] : undefined;
  const quoteId = searchParams.get('quote');

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
      <div
        className="pointer-events-none fixed inset-0 opacity-40"
        style={{
          background:
            'radial-gradient(ellipse 80% 50% at 50% 0%, var(--glow-purple) 0%, transparent 50%), radial-gradient(ellipse 50% 40% at 85% 65%, var(--glow-blue) 0%, transparent 45%)',
        }}
      />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="relative max-w-md w-full rounded-2xl border border-white/12 bg-white/[0.03] p-8 backdrop-blur-sm"
      >
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <CheckCircle className="w-12 h-12 text-white/80" />
          </motion.div>
          <h1 className="text-3xl font-bold mb-4">Thank you!</h1>
          <p className="text-white/55">
            {livingProduct
              ? `Your ${livingProduct.name} subscription is confirmed.`
              : 'Your order has been successfully processed.'}
          </p>
        </div>

        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
          <h3 className="font-semibold mb-2">Next steps</h3>
          <ul className="space-y-3 text-sm text-white/55">
            <li className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-white/50 shrink-0 mt-0.5" />
              <span>
                {livingProduct?.blurb ??
                  'Check your email for order confirmation and next steps.'}
              </span>
            </li>
          </ul>
        </div>

        {sessionId && (
          <p className="text-center mt-6 text-xs text-white/35">Reference: {sessionId}</p>
        )}

        {quoteId && (
          <p className="text-center mt-4 text-sm text-white/45">
            <Link to={`/build/quote/${quoteId}`} className="underline hover:text-white">
              View your saved quote
            </Link>
          </p>
        )}

        <div className="mt-8 text-center">
          <Link
            to={
              livingProduct?.name.startsWith('Project')
                ? quoteId
                  ? `/build/quote/${quoteId}`
                  : '/build'
                : productKey === 'blood-evaluation-deposit'
                  ? '/blood'
                  : livingProduct
                    ? '/living-website'
                    : '/'
            }
            className="text-sm text-white/60 underline hover:text-white"
          >
            {livingProduct?.name.startsWith('Project')
              ? 'Back to your quote'
              : productKey === 'blood-evaluation-deposit'
                ? 'Back to BLOOD'
                : livingProduct
                  ? 'Back to Living Website'
                  : 'Return home'}
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default SuccessPage;
